/**
 * The one place that talks to the API.
 *
 * Access tokens stay in memory only, so a script injected into the page cannot
 * read them out of storage. The refresh token has to survive a reload, and the
 * API does not use cookies, so it goes in localStorage.
 *
 * The API rotates refresh tokens on every use and revokes the old one. Two
 * requests failing at 401 at the same time would therefore race: the first
 * rotation invalidates the token the second request is holding. `refreshOnce`
 * collapses that into a single in-flight refresh that both requests await.
 */

const REFRESH_KEY = 'entry.refreshToken';

let accessToken = null;
let onUnauthorized = null;
let refreshing = null;

export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

export class ApiError extends Error {
  constructor(message, { status, code, details } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const setRefreshToken = (token) => {
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
};

async function readError(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    // Some responses (proxy errors, HTML error pages) have no JSON body.
  }
  const message =
    body?.message ||
    body?.error ||
    (res.status === 401
      ? 'Your session has expired. Please sign in again.'
      : res.status === 403
        ? 'You do not have permission to do that.'
        : res.status === 429
          ? 'Too many attempts. Please wait a moment and try again.'
          : `Request failed (${res.status}).`);
  return new ApiError(message, {
    status: res.status,
    code: body?.code,
    details: body?.errors
  });
}

async function refreshOnce() {
  if (refreshing) return refreshing;
  const token = getRefreshToken();
  if (!token) return null;

  refreshing = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: token })
      });
      if (!res.ok) {
        setRefreshToken(null);
        accessToken = null;
        return null;
      }
      const data = await res.json();
      accessToken = data.accessToken;
      setRefreshToken(data.refreshToken);
      return data;
    } catch {
      // A network failure must not destroy the token; the caller retries later.
      return null;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

async function send(path, { method = 'GET', body, headers = {}, isRetry = false, signal, auth = true } = {}) {
  const init = { method, headers: { ...headers }, signal, credentials: 'same-origin' };

  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  // `auth: false` means the caller wants a genuinely anonymous request, so the
  // access token must not be attached even when one is held.
  if (auth && accessToken) init.headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(path, init);

  if (res.status === 401 && !isRetry && auth && getRefreshToken()) {
    const refreshed = await refreshOnce();
    if (refreshed) return send(path, { method, body, headers, isRetry: true, signal, auth });
  }
  if (res.status === 401 && !isRetry && auth) {
    onUnauthorized?.();
  }
  if (!res.ok) throw await readError(res);
  if (res.status === 204) return null;

  const type = res.headers.get('content-type') || '';
  return type.includes('application/json') ? res.json() : res.text();
}

/** Authenticated call. Refreshes and retries once when the access token expired. */
export const api = (path, options) => send(path, options);

/** Public call. Sends no credentials at all, and never refreshes on a 401. */
export const publicApi = (path, options = {}) => send(path, { ...options, auth: false });

/**
 * Server-sent events. The chat endpoint streams, so it cannot go through
 * `send`, which waits for a complete JSON body. A 401 is still refreshed and
 * retried, because a token can expire mid-conversation.
 */
export async function stream(path, body, onChunk) {
  const attempt = async (isRetry) => {
    const res = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
      },
      body: JSON.stringify(body)
    });

    if (res.status === 401 && !isRetry && getRefreshToken()) {
      const refreshed = await refreshOnce();
      if (refreshed) return attempt(true);
    }
    if (!res.ok) throw await readError(res);
    if (!res.body) throw new ApiError('This browser cannot read a streamed reply.');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Events are separated by a blank line, and a chunk can split one in half.
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const part of parts) {
        let name = 'message';
        let data = null;
        for (const line of part.split('\n')) {
          if (line.startsWith('event:')) name = line.slice(6).trim();
          else if (line.startsWith('data:')) data = line.slice(5).trim();
        }
        if (!data) continue;
        try {
          onChunk({ event: name, ...JSON.parse(data) });
        } catch {
          /* a keep-alive or partial frame, so nothing to report */
        }
      }
    }
  };

  return attempt(false);
}

/** Called after signup/login/verify/refresh to adopt the new token pair. */
export const adoptSession = ({ accessToken: access, refreshToken: refresh }) => {
  accessToken = access;
  setRefreshToken(refresh);
};

/** Restores the session on page load using the stored refresh token. */
export const restoreSession = async () => {
  if (!getRefreshToken()) return null;
  return refreshOnce();
};

export const clearSession = () => {
  accessToken = null;
  setRefreshToken(null);
};

/** Builds a query string, dropping empty values so the URL stays clean. */
export const query = (params) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) value.forEach((v) => search.append(key, v));
    else search.append(key, value);
  }
  const out = search.toString();
  return out ? `?${out}` : '';
};
